import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarBlank, Clock, User, Circle } from "@phosphor-icons/react";
import { format } from "date-fns";

const TimeclockPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  useEffect(() => { fetchData(); }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [histRes, sumRes] = await Promise.all([
        axios.get(`${API}/timeclock/history?date=${dateStr}`),
        axios.get(`${API}/timeclock/summary?start_date=${dateStr}&end_date=${dateStr}`)
      ]);
      setHistory(histRes.data);
      setSummary(sumRes.data);
    } catch (error) {
      console.error("Failed to load timeclock:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "-";
    try { return new Date(isoStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return isoStr; }
  };

  const totalHours = summary.reduce((s, u) => s + u.total_hours, 0);
  const clockedIn = summary.filter(u => u.currently_clocked_in).length;

  return (
    <div className="min-h-screen bg-background" data-testid="timeclock-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium">Time Clock</h1>
            <p className="text-muted-foreground">Staff clock-in/out records and hours tracking</p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="timeclock-date">
                <CalendarBlank size={18} /> {format(selectedDate, "MMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center"><User size={24} className="text-primary" weight="fill" /></div>
              <div><p className="text-sm text-muted-foreground">Staff Worked</p><p className="font-mono text-2xl font-medium">{summary.length}</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><Circle size={24} className="text-green-600" weight="fill" /></div>
              <div><p className="text-sm text-muted-foreground">Currently In</p><p className="font-mono text-2xl font-medium">{clockedIn}</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Clock size={24} className="text-blue-600" weight="fill" /></div>
              <div><p className="text-sm text-muted-foreground">Total Hours</p><p className="font-mono text-2xl font-medium">{totalHours.toFixed(1)}h</p></div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>
        ) : (
          <>
            {/* Per-Employee Summary */}
            <Card className="mb-6">
              <CardHeader><CardTitle className="font-heading text-lg">Employee Hours</CardTitle></CardHeader>
              <CardContent>
                {summary.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground"><Clock size={48} className="mx-auto mb-2 opacity-30" /><p>No time entries for this date</p></div>
                ) : (
                  <div className="space-y-2">
                    {summary.map(emp => (
                      <div key={emp.user_id} className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl border border-border" data-testid={`emp-${emp.user_id}`}>
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center"><User size={20} className="text-primary" weight="bold" /></div>
                            {emp.currently_clocked_in && (
                              <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{emp.user_name}</p>
                            <p className="text-sm text-muted-foreground capitalize">{emp.role}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-mono text-lg font-medium">{emp.total_hours.toFixed(2)}h</p>
                            <p className="text-xs text-muted-foreground">{emp.entries_count} punch(es)</p>
                          </div>
                          {emp.currently_clocked_in && (
                            <Badge className="bg-green-100 text-green-700 border-green-200">Clocked In</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Detailed Entries */}
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Punch Detail</CardTitle></CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No punches</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left text-muted-foreground">
                          <th className="py-2 pr-4 font-medium">Employee</th>
                          <th className="py-2 pr-4 font-medium">Role</th>
                          <th className="py-2 pr-4 font-medium">Device</th>
                          <th className="py-2 pr-4 font-medium">Clock In</th>
                          <th className="py-2 pr-4 font-medium">Clock Out</th>
                          <th className="py-2 font-medium text-right">Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map(entry => (
                          <tr key={entry.id} className="border-b border-border/50" data-testid={`punch-${entry.id}`}>
                            <td className="py-3 pr-4 font-medium">{entry.user_name}</td>
                            <td className="py-3 pr-4 capitalize text-muted-foreground">{entry.role}</td>
                            <td className="py-3 pr-4 text-xs text-muted-foreground max-w-[150px] truncate" title={entry.device || ""}>{entry.device ? entry.device.split(/[(/]/)[0].trim() : "—"}</td>
                            <td className="py-3 pr-4 font-mono">{formatTime(entry.clock_in)}</td>
                            <td className="py-3 pr-4 font-mono">
                              {entry.clock_out ? formatTime(entry.clock_out) : <Badge className="bg-green-100 text-green-700 border-0 text-xs">Active</Badge>}
                            </td>
                            <td className="py-3 text-right font-mono">{entry.hours_worked ? `${entry.hours_worked.toFixed(2)}h` : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default TimeclockPage;
